"use client"

import { useEffect } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import {
  mergeOwnedIntoSearchParams,
  serializeHomeInvoiceListState,
  serializeInvoiceListState,
  FILTER_URL_KEYS,
  HOME_FILTER_URL_KEYS,
  type InvoiceListFilterState,
  type InvoiceListUrlState,
} from "./filters"

/** Sort a query string so key ordering never reads as a change on its own. */
function normalize(params: URLSearchParams): string {
  const sorted = new URLSearchParams(params)
  sorted.sort()
  return sorted.toString()
}

/**
 * Keep a fixed set of owned query keys in sync with the given serialized
 * values while preserving every foreign query param. Only rewrites when the
 * owned keys actually changed, guarding against a ``router.replace`` loop.
 *
 * The equality check compares the *sorted* query strings so that the merge
 * moving owned keys to the end (delete-then-set) never reads as a change on
 * its own — otherwise a URL whose foreign params interleave the owned ones
 * would churn once per render. The dependency is the serialized string, not
 * the freshly-allocated `owned` params object, so a settled state does not
 * re-run the effect.
 *
 * Because the URL is the source of truth, a list → detail → back navigation
 * restores the full selection: the list surface remounts and re-seeds every
 * control from the restored query string.
 */
function useOwnedSearchParamsSync(
  ownedKeys: readonly string[],
  owned: URLSearchParams,
): void {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const ownedSerialized = owned.toString()

  useEffect(() => {
    const current = new URLSearchParams(searchParams?.toString() ?? "")
    const merged = mergeOwnedIntoSearchParams(
      current,
      ownedKeys,
      new URLSearchParams(ownedSerialized),
    )
    if (normalize(merged) === normalize(current)) return
    const qs = merged.toString()
    router.replace(qs ? `${pathname}?${qs}` : pathname)
  }, [ownedKeys, ownedSerialized, pathname, router, searchParams])
}

/**
 * Mirror the main `/invoices` filter + page state into the URL. Callers pass
 * the *committed* state (e.g. the debounced search value) so the URL tracks
 * what is actually queried rather than each keystroke.
 */
export function useInvoiceFiltersUrlSync(state: InvoiceListUrlState): void {
  useOwnedSearchParamsSync(FILTER_URL_KEYS, serializeInvoiceListState(state))
}

/**
 * Mirror the home "My Invoices" filter + page state into the URL. Its owned
 * key set differs from the main page (created-at window, course, page size;
 * no owner scope), but the loop-guard is shared.
 */
export function useHomeInvoiceFiltersUrlSync(
  state: InvoiceListFilterState,
): void {
  useOwnedSearchParamsSync(
    HOME_FILTER_URL_KEYS,
    serializeHomeInvoiceListState(state),
  )
}
